/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/


import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { sendMessageToGemini } from '../services/geminiService';
import { ChatMessage } from '../types';

interface AIChatProps {
  isOpenExternal?: boolean;
  onCloseExternal?: () => void;
  initialMessage?: string;
  lang?: 'es' | 'en';
}

import PixelAvatar from './PixelAvatar';

const AIChat: React.FC<AIChatProps> = ({ isOpenExternal, onCloseExternal, initialMessage, lang = 'es' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isTalking, setIsTalking] = useState(false);
  const [isNodding, setIsNodding] = useState(false);
  const [isSurprised, setIsSurprised] = useState(false);
  const [currentSection, setCurrentSection] = useState<'gallery' | 'written' | 'artist' | 'default'>('default');
  const chatContainerRef = useRef<HTMLDivElement>(null);

  const location = useLocation();

  // Detect current section for avatar details
  useEffect(() => {
    const path = location.pathname;
    const hash = location.hash;

    if (path.includes('gallery') || hash.includes('gallery')) setCurrentSection('gallery');
    else if (path.includes('works') || hash.includes('escritos') || hash.includes('works')) setCurrentSection('written');
    else if (path.includes('artist') || hash.includes('artist')) setCurrentSection('artist');
    else setCurrentSection('default');
  }, [location]);



  // Set initial welcome message based on language
  useEffect(() => {
    const welcomeText = lang === 'es'
      ? '¡Hola! Soy el asistente virtual de CHIMELISART. ✨ ¿Deseas conocer más sobre la obra de Francesc?'
      : 'Hello! I am the CHIMELISART virtual assistant. ✨ Would you like to learn more about Francesc\'s work?';

    setMessages(prev => {
      if (prev.length === 0) return [{ role: 'model', text: welcomeText }];
      return prev;
    });
  }, [lang]);

  useEffect(() => {
    if (isOpenExternal !== undefined) {
      setIsOpen(isOpenExternal);
    }
  }, [isOpenExternal]);

  // Listen for external triggers
  useEffect(() => {
    const handleTrigger = (e: any) => {
      if (e.detail?.message) {
        setInput(e.detail.message);
      }
      setIsOpen(true);

      if (e.detail?.reaction === 'surprised') {
        setIsSurprised(true);
        setTimeout(() => setIsSurprised(false), 1500);
      } else if (e.detail?.reaction === 'nodding') {
        setIsNodding(true);
        setTimeout(() => setIsNodding(false), 1500);
      }
    };

    window.addEventListener('chimelis-ai-trigger', handleTrigger);
    return () => window.removeEventListener('chimelis-ai-trigger', handleTrigger);
  }, []);

  const handleToggle = () => {
    const newState = !isOpen;
    setIsOpen(newState);
    if (!newState && onCloseExternal) {
      onCloseExternal();
    }
  };

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      const { scrollHeight, clientHeight } = chatContainerRef.current;
      chatContainerRef.current.scrollTo({
        top: scrollHeight - clientHeight,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isOpen]);

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage: ChatMessage = { role: 'user', text: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    setTimeout(scrollToBottom, 100);

    const responseText = await sendMessageToGemini(input);

    setMessages(prev => [...prev, { role: 'model', text: responseText }]);
    setIsLoading(false);

    // Animate talking for a short duration when response arrives
    setIsTalking(true);
    setTimeout(() => setIsTalking(false), 2000);
  };

  return (
    <div className="fixed bottom-4 right-4 md:bottom-8 md:right-8 z-50 flex flex-col items-end pointer-events-auto">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9, rotate: -5 }}
            animate={{ opacity: 1, y: 0, scale: 1, rotate: 0 }}
            exit={{ opacity: 0, y: 20, scale: 0.9, rotate: 5 }}
            className="mb-4 w-[90vw] md:w-96 bg-[#1c1917]/95 backdrop-blur-xl border-2 rounded-2xl overflow-hidden shadow-2xl origin-bottom-right border-stone-900"
          >
            {/* Header */}
            <div className="p-4 flex justify-between items-center border-b border-white/10 bg-stone-900">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg overflow-hidden border border-white/30 bg-white/10">
                  <PixelAvatar
                    isThinking={isLoading}
                    isTalking={isTalking}
                    isNodding={isNodding}
                    isSurprised={isSurprised}
                    section={currentSection}
                  />
                </div>
                <h3 className="font-heading font-bold text-white tracking-wider text-sm uppercase">CHIMELISART AI</h3>
              </div>
              <button onClick={handleToggle} className="text-white/50 hover:text-white" data-hover="true" aria-label="Close chat">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Messages */}
            <div
              ref={chatContainerRef}
              className="h-64 md:h-80 overflow-y-auto p-4 space-y-4 scroll-smooth bg-black/40"
            >
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] p-3 rounded-2xl text-sm leading-relaxed ${msg.role === 'user'
                      ? 'bg-amber-400 text-black rounded-tr-sm shadow-md'
                      : 'bg-[#292524] text-[#d6d3d1] rounded-tl-sm border border-white/5 shadow-sm'
                      }`}
                  >
                    {msg.text}
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-[#292524] p-3 rounded-2xl rounded-tl-sm border border-white/5 flex gap-1.5 items-center">
                    <span className="w-1.5 h-1.5 rounded-full animate-bounce anim-delay-0 bg-amber-400" />
                    <span className="w-1.5 h-1.5 rounded-full animate-bounce anim-delay-150 bg-amber-400" />
                    <span className="w-1.5 h-1.5 rounded-full animate-bounce anim-delay-300 bg-amber-400" />
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <div className="p-3 border-t border-white/10 bg-[#1c1917]">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  placeholder={lang === 'es' ? "Escribe tu consulta sobre la obra..." : "Type your inquiry about the art..."}
                  className="flex-1 bg-black/40 text-white placeholder-white/30 text-sm focus:outline-none rounded-xl px-4 py-2 border border-white/5 focus:border-white/20 transition-colors"
                />
                <button
                  onClick={handleSend}
                  disabled={isLoading || !input.trim()}
                  className="p-2 rounded-xl transition-colors disabled:opacity-50 text-black shadow-lg bg-amber-400"
                  data-hover="true"
                  aria-label="Send message"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={handleToggle}
        className="w-20 h-20 md:w-24 md:h-24 bg-transparent flex items-center justify-center z-50 group hover:cursor-pointer drop-shadow-2xl"
        data-hover="true"
        aria-label={isOpen ? "Close chat " : "Open chat helper"}
        animate={{
          y: [0, -8, 0],
          transition: { repeat: Infinity, duration: 4, ease: "easeInOut" }
        }}
      >
        <PixelAvatar
          isThinking={isLoading}
          isTalking={isTalking}
          isNodding={isNodding}
          isSurprised={isSurprised}
          section={currentSection}
        />
      </motion.button>
    </div>
  );
};

export default AIChat;
